import type { ApiColumn, PaginationMeta } from "@/types/api";
import {
  type ScreenerLocation,
  type ScreenerLocationStatusType,
  ScreenerLocationStatusTypeWithUnpublished,
} from "@/types/screener-location";
import { UserListUser } from "@/types/user";
import type { Nullable } from "@/types/utils";

export enum ScreenerStatus {
  Deleted = "deleted",
  Draft = "draft",
  Preview = "preview",
  Published = "published",
  Unpublished = "unpublished",
}

// The derived status is determined on the BE taking into account both the
// screener status and its location statuses. Only screeners with a version
// number (referencing the version of that screener instance, NOT ScreenerType)
// of 1 can be in draft status
export enum DerivedScreenerStatus {
  Draft = "draft",
  Paused = "paused",
  Live = "live",
  Mixed = "mixed",
}

// When adding support for new Screener Versions, confirm that the
// `isLegacyScreenerType` util in utils/screener-builder is up to date.
export enum ScreenerType {
  // Initial CSV types
  v1 = "1.0",
  v2 = "2.0",
  // "m" indicates a multilingual screener
  v2m = "2.0m",
  // Initial AI builder type
  v2ai = "2.0ai",
  // 2.0aim combines AI Builder with multilingual support
  v2aim = "2.0aim",
  // 2.0.6 enabled custom scoring for CSV screeners
  v206 = "2.0.6",
  v206m = "2.0.6m",
}

export enum CompetencyFit {
  VeryHigh = "Very high",
  High = "High",
  Medium = "Medium",
  Low = "Low",
  VeryLow = "Very low",
  Absent = "Absent",
  NotAsked = "Not Asked",
}

export interface Competency {
  // Raw score is only available to internal users
  rawScore?: Nullable<number>;
  score?: CompetencyFit;
  quote: string;
}

export const COMPETENCY_FIT_NUMERIC_MAP: Record<CompetencyFit, number> = {
  [CompetencyFit.VeryHigh]: 6,
  [CompetencyFit.High]: 5,
  [CompetencyFit.Medium]: 4,
  [CompetencyFit.Low]: 3,
  [CompetencyFit.VeryLow]: 2,
  [CompetencyFit.Absent]: 1,
  [CompetencyFit.NotAsked]: 0,
};

export interface Competencies {
  [name: string]: Competency;
}

export enum SessionStatus {
  Empty = "empty",
  Partial = "partial",
  Completed = "completed",
}

// "Root" question type to distinguish from subtypes such as Number or YesNo
// used within basic questions. From a screener builder Review page perspective
// Assessment questions are competencies
export enum RootQuestionTypes {
  Assessment = "assessment",
  Basic = "basic",
}

export enum MessageSenders {
  Candidate = "Candidate",
  Screener = "Screener",
}

export interface Question {
  createdAt: string;
  message: string;
  questionId: string;
  sender: MessageSenders;
  type: RootQuestionTypes;
}

export interface AssessmentQuestion extends Question {
  type: RootQuestionTypes.Assessment;
}

export interface BasicQuestion extends Question {
  answerStandardized?: string;
  internalLabel: string;
  qualifyingPassed: Nullable<boolean>;
  questionOrder: number;
  showInSummary: boolean;
  type: RootQuestionTypes.Basic;
}

export enum HighlightColor {
  Red = "red",
  Blue = "blue",
  Orange = "orange",
}

export const QUALIFYING_PASS_RATE_STRING_PATH =
  "qualifyingSummary.qualifyingPassRate";

// The by email response is formatted slightly differently from the by location response. We split them here for API
// responses and massage everything into `Screening` before passing it into the rest of the app.
interface BaseScreening {
  assessmentQuestions?: AssessmentQuestion[];
  basicQuestions?: BasicQuestion[];
  candidateEmail: string;
  candidateLink?: string;
  candidateName: string;
  candidatePhone: string;
  createdAt: string;
  highlightColor?: HighlightColor;
  jobTitle: string;
  language?: string;
  locationName: string;
  overallFit?: CompetencyFit;
  pdfLink?: string;
  pdfLocation?: string;
  positionLink?: string;
  // Qualifying summary is nullable for empty sessions, but should be available
  // in all other cases.
  qualifyingSummary: Nullable<{
    // Pass rate and questions passed are null when there are no qualifying
    // questions
    qualifyingPassRate: Nullable<number>;
    qualifyingQuestions: number;
    qualifyingQuestionsPassed: Nullable<number>;
  }>;
  // Raw scores are only available to internal users
  rawQualifyingQuestionsQualified?: Nullable<number>;
  rawQualifyingQuestionsTotal?: Nullable<number>;
  rawOverallFitScore?: Nullable<number>;
  screenerDisplayName?: string;
  screenerLocation?: ScreenerLocation;
  sessionId: string;
  sessionStatus: SessionStatus;
  time: string;
  totalTimeInSeconds: number;
  totalWords: number;
  viewedAt?: string;
  voiceQuestions: Question[];
  words: string;
}

export interface Screening extends BaseScreening {
  competencies?: {
    [competency: string]: Competency;
  };
  // These are added onto screenings fetched for the table, but will not be
  // available on screenings fetched by user (as there would be no others to
  // compare them with)
  highTimeSpent?: boolean;
  highWordCount?: boolean;
  screenerVersionNumber?: number;
  showInSummary?: {
    [key: string]: string | number;
  };
}

export interface UserResponseScreening extends BaseScreening {
  competencyFields?: {
    [competency: string]: Competency;
  };
  showInSummaryFields?: {
    message: string | number;
    internalLabel: string;
  };
}

export interface CandidateResponseScreener {
  company: string;
  companyId: string;
  id: string;
  internalName: string;
  jobTitle: string;
  type: ScreenerType;
}

export interface Screener {
  createdAt: string;
  id: string;
  internalName?: string;
  isDraft: boolean;
  jobTitle: string;
  lastSubmissionDate?: string;
  location: string[];
  screenings: number;
  status: ScreenerLocationStatusType;
  unviewedSessionCount: number;
  userId?: string;
  type: ScreenerType;
  version?: string;
  wage: string;
}

export interface ArchivedScreener extends Screener {
  archivedAt: string;
}

export type ScreenerOrArchivedScreener = Screener | ArchivedScreener;

// The by email response is formatted slightly differently from the by location response. We split them here for API
// responses and massage everything into `Screening` before passing it into the rest of the app.
interface BaseCandidateResponse {
  columns: ApiColumn<Screening>[];
  screener: CandidateResponseScreener;
  screenerLocation: { location: string };
  sessionId: string;
}

export interface CandidateResponse extends BaseCandidateResponse {
  screenerSessions: Screening[];
}

export interface UserCandidateResponse extends BaseCandidateResponse {
  screenerSessions: UserResponseScreening[];
}

export interface TableScreenings extends CandidateResponse {
  availableFilters: ResultsAvailableFilters;
  pagination: PaginationMeta;
}

export interface AllScreenersResponse<T extends ArchivedScreener | Screener> {
  availableFilters: {
    jobTitles: string[];
    locations: string[];
    statuses: ScreenerLocationStatusTypeWithUnpublished[];
    users: UserListUser[];
  };
  columns: ApiColumn<T>[];
  pagination: PaginationMeta;
  screenerRows: T[];
}

export interface LanguageOption {
  code: string;
  name: string;
}

export interface ResultsAvailableFilters {
  competencies: string[];
  highlightColors: string[];
  languages: LanguageOption[];
  showInSummaryFields: string[];
}

export interface AllLocationsResponseScreener {
  company: string;
  companyId: string;
  internalName: string;
  id: string;
  jobTitle: string;
  type: string;
}

export interface CsvUploadTranslationResponse {
  success: boolean;
  message: string;
}
